import { useRef, useState } from "react";
import { string, shape, bool, number, func, oneOfType } from "prop-types";
import cx from "classnames";

const propTypes = {
  authenticated: bool.isRequired,
  submitEndPointUrl: string.isRequired,
  criteriaUrl: string,
  threadId: number,
  forumId: number,
  parentId: oneOfType([number, string]),
  itemLink: string,
  userEmailAddress: string,
  maxCharacterLength: number,
  onRestart: func.isRequired,
  initialResponseValue: string,
  dictionary: shape({
    submit: string,
    cancel: string,
    login: string,
    signUp: string,
    criteriaLink: string,
    responseLabel: string,
    responsePlaceholder: string,
    responsePlaceholderUnauthenticated: string,
    authenticatedMessage: string,
    unauthenticatedMessage: string,
    unauthenticatedTitle: string,
    successTitle: string,
    successMessage: string,
    successAction: string,
    serverError: string,
    inputError: string,
  }).isRequired,
};

const defaultProps = {
  parentId: 0,
  maxCharacterLength: 280,
};

const ResponseInterface = (props) => {
  const {
    authenticated,
    criteriaUrl,
    maxCharacterLength,
    dictionary,
    initialResponseValue,
  } = props;

  const inputRef = useRef();

  // ***************
  // STATE
  // ***************

  const [sumbittingForm, setSumbittingForm] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [response, setResponse] = useState(initialResponseValue || "");
  const [responseError, setResponseError] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [charactersLeft, setCharactersLeft] = useState(
    maxCharacterLength - response.length,
  );
  const [showSucess, setShowSuccess] = useState(false);

  // ***************
  // HELPERS
  // ***************

  const resetApp = () => {
    setResponse("");
    setResponseError(false);
    setCharactersLeft(maxCharacterLength);
    setGeneralError("");
  };

  // ***************
  // API
  // ***************

  const submitForm = () => {
    setSumbittingForm(true);

    const {
      submitEndPointUrl,
      threadId,
      forumId,
      parentId,
      itemLink,
      userEmailAddress,
    } = props;

    const params = {
      body: response,
      parentId: parentId,
    };

    if (userEmailAddress) {
      params["userEmailAddress"] = userEmailAddress;
    }

    if (itemLink) {
      params["itemLink"] = itemLink;
    }

    if (threadId) {
      params["threadId"] = threadId;
    }

    if (forumId) {
      params["forumId"] = forumId;
    }

    fetch(submitEndPointUrl, {
      method: "POST",
      body: JSON.stringify(params),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setShowSuccess(true);
          resetApp();
        } else {
          setGeneralError(dictionary.serverError);
        }
        setSumbittingForm(false);
      })
      .catch((error) => {
        console.error(error);
        setSumbittingForm(false);
        setGeneralError(dictionary.serverError);
      });
  };

  // ***************
  // EVENTS
  // ***************

  const handleInputChange = (e) => {
    const value = e.target.value;
    setResponseError(false);
    setGeneralError("");

    if (value.length <= maxCharacterLength) {
      setResponse(value);
      setCharactersLeft(maxCharacterLength - value.length);
    }
  };

  const handleFocus = () => {
    setShowButtons(true);
  };

  const handleUnauthenticatedPlaceholder = () => {
    inputRef.current.placeholder =
      dictionary.responsePlaceholderUnauthenticated;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setGeneralError("");

    if (response.length <= 0) {
      setResponseError(true);
      inputRef.current.focus();
    } else {
      setResponseError(false);
      submitForm();
    }
  };

  const handleReset = () => {
    resetApp();
    setShowButtons(false);
    props.onRestart();
  };

  const handleRestart = () => {
    setShowSuccess(false);
    props.onRestart();
  };

  // ***************
  // RENDER
  // ***************

  const inputClass = cx("sc-form-item__field sc-form-item__field--textarea", {
    "sc-form-item__field--is-dirty": response.length > 0,
  });

  const loginMessage = dictionary.unauthenticatedMessage
    .replace(
      "[[LOGIN]]",
      `<button type="button" onclick="window.openGlobalModal('login')" class="btn link-style response-interface__link">${dictionary.login}</button>`,
    )
    .replace(
      "[[SIGNUP]]",
      `<button type="button" onclick="window.openGlobalModal('signup')" class="btn link-style response-interface__link">${dictionary.signUp}</button>`,
    );

  const criteriaLink = criteriaUrl
    ? `<a class="link-style response-interface__link" href="${criteriaUrl}">${dictionary.criteriaLink}</a>`
    : dictionary.criteriaLink;

  const authMessage = dictionary.authenticatedMessage.replace(
    "[[CRITERIALINK]]",
    criteriaLink,
  );

  return (
    <section className="response-interface">
      {!authenticated && !showSucess && (
        <div className="response-interface__header">
          <div className="response-interface__heading">
            {dictionary.unauthenticatedTitle}
          </div>
          <div
            className="response-interface__message"
            dangerouslySetInnerHTML={{ __html: loginMessage }}
          ></div>
        </div>
      )}
      {authenticated && !showSucess && (
        <div className="response-interface__header">
          <div
            className="response-interface__message"
            dangerouslySetInnerHTML={{ __html: authMessage }}
          ></div>
        </div>
      )}
      {showSucess && (
        <div className="response-interface__header">
          <div className="response-interface__heading response-interface__heading--light">
            {dictionary.successTitle}
          </div>
          <div className="response-interface__message">
            <p>{dictionary.successMessage}</p>
            <p>
              <button
                type="button"
                onClick={handleRestart}
                className="btn link-style response-interface__link"
              >
                {dictionary.successAction}
              </button>
            </p>
          </div>
        </div>
      )}
      {!showSucess && (
        <div className="response-interface__form-wrapper">
          <form className="response-interface__form sc-form">
            <label className="sc-form-item">
              <span className="sc-form-item__label">
                {dictionary.responseLabel}
              </span>
              {authenticated && (
                <textarea
                  ref={inputRef}
                  name="response-message"
                  className={inputClass}
                  type="text"
                  value={response}
                  aria-required="true"
                  placeholder={dictionary.responsePlaceholder}
                  onFocus={handleFocus}
                  onChange={handleInputChange}
                />
              )}
              {!authenticated && (
                <input
                  ref={inputRef}
                  name="response-message"
                  className="sc-form-item__field"
                  type="text"
                  value={response}
                  aria-required="true"
                  placeholder={dictionary.responsePlaceholder}
                  readOnly
                  onClick={handleUnauthenticatedPlaceholder}
                  onFocus={handleUnauthenticatedPlaceholder}
                />
              )}
              {responseError ? (
                <div role="alert" className="has-error sc-form-item__error-msg">
                  {dictionary.inputError}
                </div>
              ) : (
                <div className="sc-form-item__max-characters">
                  {charactersLeft}
                </div>
              )}
            </label>
            {generalError.length > 0 && (
              <div className="sc-form__general-error is-visible">
                {generalError}
              </div>
            )}
            {authenticated && showButtons && (
              <div className="sc-form__button-row response-interface__button-row">
                <button
                  type="submit"
                  className="btn btn--primary"
                  onClick={handleSubmit}
                  disabled={sumbittingForm}
                >
                  {dictionary.submit}
                </button>
                <button
                  type="reset"
                  className="btn btn--primary-ghost"
                  onClick={handleReset}
                >
                  {dictionary.cancel}
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </section>
  );
};

ResponseInterface.propTypes = propTypes;
ResponseInterface.defaultProps = defaultProps;
export default ResponseInterface;
